import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { FaStar } from 'react-icons/fa';
import { CiSearch } from "react-icons/ci";
import { BsCalendar } from 'react-icons/bs';
import { IoClose } from "react-icons/io5";
import BookAppointment from '../../components/BookAppointment';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LuPlus } from "react-icons/lu";
import * as apicall from '../../components/Apicall';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import serviceNames from '../../data/serviceNames.json';
import UserContext from '../../components/context/UserContext'

const DoctorCardSkeleton = () => (
  <div className="card h-100 shadow-sm" style={{
    borderRadius: '8px',
    border: '1px solid #E3EBF8',
    display: 'flex',
    flexDirection: 'column'
  }}>
    <div className="position-relative" style={{ padding: '12px', flex: '0 0 auto' }}>
      <Skeleton height={110} style={{ borderRadius: '10px' }} />
    </div>
    <div className="card-body p-3" style={{ flex: '1 1 auto', marginTop: '0px' }}>
      <Skeleton height={24} width="80%" className="mb-2" />
      <Skeleton height={20} width="60%" className="mb-2" />
      <Skeleton height={20} width="70%" className="mb-2" />
      <Skeleton height={20} width="50%" className="mb-2" />
      <Skeleton height={35} width="100%" style={{ borderRadius: '16px' }} />
    </div>
  </div>
);

const Doctors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [specializations, setSpecializations] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedSpecialization, setSelectedSpecialization] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalDoctors, setTotalDoctors] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const { vendor, refreshVendorData } = useContext(UserContext);
  const [serviceId, setServiceId] = useState('');
  const [serviceInt, setServiceInt] = useState(''); // Default to 7 for doctors
  const ITEMS_PER_PAGE = 30;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Fetch specializations when serviceId changes
  useEffect(() => {
    if (serviceId) {
      fetchSpecializations();
    }
  }, [serviceId]);

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      await refreshVendorData();
      
      const initialSearchQuery = location.state?.searchQuery;
      if (initialSearchQuery) {
        setSearchQuery(initialSearchQuery);
        setDebouncedSearch(initialSearchQuery);
      }
      
      setInitialLoadComplete(true);
    };

    initializeData();
  }, []);

  // Set serviceId when vendor is available
  useEffect(() => {
    if (vendor) {
      setServiceId(vendor.service);
      setServiceInt(vendor.service_id);

    }
  }, [vendor]);

  // Debounce search query
  useEffect(() => {
    if (initialLoadComplete) {
      const timer = setTimeout(() => {
        setDebouncedSearch(searchQuery);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, initialLoadComplete]);

  // Fetch doctors when filters change
  useEffect(() => {
    if (initialLoadComplete) {
      setPage(1);
      setDoctors([]);
      setFilteredDoctors([]);
      setHasMore(true);
      
      const filters = {
        specialization: selectedSpecialization,
        gender: selectedGender,
        search: debouncedSearch,
        date: selectedDate,
        page: 1
      };

      fetchDoctors(filters);
    }
  }, [selectedSpecialization, selectedGender, debouncedSearch, selectedDate, initialLoadComplete]);

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getRandomColor = () => {
    const colors = [
      '#FFE4E1', // Misty Rose
      '#E6E6FA', // Lavender
      '#F0FFF0', // Honeydew
      '#F0F8FF', // Alice Blue
      '#FFF0F5', // Lavender Blush
      '#F5F5DC', // Beige
      '#E0FFFF', // Light Cyan
      '#FFE4B5', // Moccasin
      '#F0FFFF', // Azure
      '#F8F8FF'  // Ghost White
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const fetchDoctors = async (filters = {}) => {
    if (filters.page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let url = `get_service_providers?vendor_id=${localStorage.getItem('vendorId')}&limit=${ITEMS_PER_PAGE}&page=${filters.page || 1}`;

      if (filters.specialization) {
        url += `&specilisation=${filters.specialization}`;
      }
      if (filters.gender) {
        url += `&gender=${filters.gender === 'male' ? 1 : 2}`;
      }
      if (filters.search) {
        url += `&name=${encodeURIComponent(filters.search.trim())}`;
      }
      if (filters.date) {
        const formattedDate = formatDateForAPI(filters.date);
        url += `&date=${formattedDate}`;
      }

      const response = await apicall.get(url);

      if (response && response.status === 200 && Array.isArray(response.data)) {
        const formattedDoctors = response.data.map(doctor => ({
          ...doctor,
          id: doctor.id,
          user_id: doctor.user_id,
          name: `${doctor?.first_name || ''} ${doctor?.last_name || ''}`.trim(),
          email: doctor.email,
          phone: doctor.phone_number,
          gender: doctor.gender === 1 ? 'Male' : 'Female',
          specialization: doctor.specilisation_name || doctor.specialization,
          specialization_id: doctor.specilisation_id || doctor.specilisation,
          education: doctor.education,
          experience: calculateExperience(doctor.practice_start),
          fee: doctor.fee,
          profile_image: doctor.profile_image,
          rating: doctor.average_rating ?? 0
        }));

        if (filters.page === 1) {
          setDoctors(formattedDoctors);
          setFilteredDoctors(formattedDoctors);
        } else {
          setDoctors(prev => [...prev, ...formattedDoctors]);
          setFilteredDoctors(prev => [...prev, ...formattedDoctors]);
        }

        if (response.pagination) {
          setTotalDoctors(response.pagination.total || 0);
          setTotalPages(response.pagination.total_pages || 1);
          setHasMore(filters.page < (response.pagination.total_pages || 1));
        } else {
          setTotalDoctors(response.total_count || formattedDoctors.length);
          setHasMore(formattedDoctors.length === ITEMS_PER_PAGE);
        }
      } else {
        console.error('Invalid response format:', response);
        if (filters.page === 1) {
          setDoctors([]);
          setFilteredDoctors([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      if (filters.page === 1) {
        setDoctors([]);
        setFilteredDoctors([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  
  const calculateExperience = (practiceStart) => {
    if (!practiceStart) return '0';
    const startDate = new Date(practiceStart);
    const currentDate = new Date();
    const years = currentDate.getFullYear() - startDate.getFullYear();
    return `${years} Years Experience`;
  };

  const fetchSpecializations = async () => {
    try {
      const response = await apicall.get(`get_service_offerings/${serviceId}`);
      if (response.status === 200) {
        setSpecializations(response.data);
      }
    } catch (error) {
      console.error('Error fetching specializations:', error);
    }
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'D';
  };

  const getSpecializationName = (id) => {
    const spec = specializations.find(s => s.id === id);
    return spec ? spec.name : 'General';
  };

  const handleAddDoctor = () => {
    navigate('/doctors/add');
  };

  const handleCreateAvailability = () => {
    navigate('/doctors/create-availability');
  };

  const handleBookNow = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBooking(true);
  };

  const handleCloseBooking = () => {
    setShowBooking(false);
    setSelectedDoctor(null);
  };

  const handleViewProfile = (doctor, serviceName) => {
    navigate(`/doctors/profile/${doctor.id}`, { state: { doctorData: doctor, serviceNames: serviceName } });
  };

  const handleSpecializationChange = (e) => {
    const value = e.target.value;
    setSelectedSpecialization(value);
  };

  const handleGenderChange = (e) => {
    const value = e.target.value;
    setSelectedGender(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchDoctors({
        specialization: selectedSpecialization,
        gender: selectedGender,
        search: searchQuery,
        date: selectedDate,
        page: 1
      });
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const handleClearFilters = () => {
    setSelectedSpecialization('');
    setSelectedGender('');
    setSearchQuery('');
    setSelectedDate(null);
    fetchDoctors(); 
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleScroll = useCallback(() => {
    const scrollPosition = window.innerHeight + document.documentElement.scrollTop;
    const documentHeight = document.documentElement.offsetHeight;
    const scrollThreshold = 100; 

    if (documentHeight - scrollPosition < scrollThreshold) {

      if (!loadingMore && hasMore && page < totalPages) {
        const nextPage = page + 1;

        setPage(nextPage);
        fetchDoctors({
          specialization: selectedSpecialization,
          gender: selectedGender,
          search: debouncedSearch,
          date: selectedDate,
          page: nextPage
        });
      }
    }
  }, [loadingMore, hasMore, page, totalPages, selectedSpecialization, selectedGender, debouncedSearch, selectedDate]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Get service name based on service_id
  const getServiceName = (names) => {
    const service = serviceNames.service_names.find(s => s.service_id === Number(serviceInt)); 
    return service ? service[names] : '';
  };

  return (
    <div className="container-fluid px-3" style={{ backgroundColor: '#F2F5FA' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 text-dark" style={{ fontSize: '24px', fontWeight: '600' }}>{getServiceName('name')}</h4>
        {loading ? (
          <div className="d-flex gap-2">
            <Skeleton width={140} height={40} />
            <Skeleton width={120} height={40} />
          </div>
        ) : (
          <div className="d-flex gap-3">
            <button
              className="btn  d-flex align-items-center custom-btn btn-larges"
              style={{
                fontSize: '13px', fontWeight: '500'
              }}
              onClick={handleCreateAvailability}
            >
              Create Availability
            </button>
            <button
              className="btn btn-danger d-flex align-items-center gap-2 custom-btn btn-large"
              style={{
                fontSize: '13px',
              }}
              onClick={handleAddDoctor}
            >
              <LuPlus size={18} /> Add {getServiceName('name')}
            </button>
          </div>
        )}
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          {loading ? (
            <Skeleton height={40} />
          ) : (
            <div className="position-relative">
              <CiSearch
                className="position-absolute top-50 start-0 translate-middle-y ms-3"
              />              <input
                type="text"
                className="form-control custom-input-search"
                placeholder={`Search for ${getServiceName('name')}`}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{
                  paddingRight: searchQuery ? '36px' : '12px',
                }}
              />
              {searchQuery && (
                <IoClose
                  className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"
                  style={{ cursor: 'pointer', fontSize: '20px' }}
                  onClick={handleClearSearch}
                />

              )}
            </div>
          )}
        </div>

        <div className="col d-flex gap-2">
          {loading ? (
            <>
              <Skeleton width={60} height={24} />
              <Skeleton width={140} height={40} />
              <Skeleton width={140} height={40} />
              <Skeleton width={140} height={40} />
            </>
          ) : (
            <>
              <div className="d-flex align-items-center text-dark form-label" >Filter by:</div>
              <Form.Select
                value={selectedSpecialization}
                onChange={handleSpecializationChange}
                className='custom-select width-large'

              >
                <option value="">All Specializations</option>
                {specializations.map((spec) => (
                  <option key={spec.id} value={spec.id}>
                    {spec.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                value={selectedGender}
                onChange={handleGenderChange}
                className='custom-select  width-small'
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </Form.Select>
              <div className="position-relative">
                <button
                  className="btn btn-light d-flex justify-content-between align-items-center border-box custom-select  width-small"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                >
                  {selectedDate ? selectedDate.toLocaleDateString() : 'Availability'}
                  <BsCalendar style={{ fontSize: '14px', color: '#000000' }} />
                </button>
                {showDatePicker && (
                  <div className="position-absolute" style={{ zIndex: 1000, top: '100%', left: 0, marginTop: '4px' }}>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      inline
                      minDate={new Date()}
                    />
                  </div>
                )}
              </div>
              {(selectedSpecialization || selectedGender || searchQuery || selectedDate) && (
                <button
                  className="btn btn-link"
                  onClick={handleClearFilters}
                  style={{
                    height: '40px',
                    fontSize: '14px',
                    color: '#DC3545',
                    textDecoration: 'none'
                  }}
                >
                  Clear Filters
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton height={24} width={150} className="mb-4" />
      ) : (
        <p className="text-muted mb-4" style={{ fontSize: '14px', fontWeight: '400', color: '#6C757D' }}>
          Total Doctors: {totalDoctors} (Page {page} of {totalPages})
        </p>
      )}

      <div className="row g-4" style={{ margin: '0px -8px', marginTop: '-30px' }}>
        {loading ? (
          Array(8).fill(0).map((_, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
              <DoctorCardSkeleton />
            </div>
          ))
        ) : (
          <>
            {filteredDoctors.map(doctor => (
              <div key={doctor.id} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
                <div className="card h-100 shadow-sm" style={{
                  borderRadius: '8px',
                  border: '1px solid #E3EBF8',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div className="position-relative" style={{ padding: '12px', flex: '0 0 auto' }}>
                    {doctor.profile_image && doctor.profile_image !== 'undefined' ? (
                      <div style={{
                        width: '100%',
                        height: '110px',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        position: 'relative',
                      }}>
                        {/* Blurred background image */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            left: '-5px',
                            right: '-5px',
                            bottom: '-5px',
                            backgroundImage: `url(${doctor.profile_image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            filter: 'blur(8px)',
                            opacity: '0.7',
                          }}
                        />
                        {/* Main image */}
                        <div style={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}>
                          <img
                            src={doctor.profile_image}
                            alt={doctor.name}
                            className="profile-image"
                            onError={(e) => {
                              const parentDiv = e.target.parentElement.parentElement;
                              parentDiv.innerHTML = `
                            <div class="image-fallback" style="background-color: ${getRandomColor()}">
                            ${getInitials(doctor.name)}
                              </div>`;
                            }}
                          />

                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '110px',
                          backgroundColor: getRandomColor(),
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '48px',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      >
                        {getInitials(doctor.name)}
                      </div>
                    )}
                    <span className="position-absolute" style={{
                      top: '16px',
                      right: '16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '10px',
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      width: 'fit-content',
                      height: '22px',
                      zIndex: 2,
                      backdropFilter: 'blur(4px)'
                    }}>
                      <FaStar className="text-warning" style={{ fontSize: '10px' }} />
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#212529' }}>{doctor.rating}</span>
                    </span>
                  </div>
                  <div className="card-body p-3" style={{ flex: '1 1 auto', marginTop: '0px' }}>
                    <h5 className="card-title mb-1 doctor-name-title text-dark">
                      {getServiceName("shortName")}.{doctor.name.charAt(0).toUpperCase() + doctor.name.slice(1).toLowerCase()}
                    </h5>

                    <p className="mb-1 doctor-info-text">
                      {getSpecializationName(doctor.specialization_id)
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                    </p>

                    <p className="mb-1 doctor-info-text">
                      {doctor.experience}
                    </p>

                    <p className="mb-3 doctor-info-text">
                      Consultation Fee: <strong>AED {doctor.fee}</strong>
                    </p>


                    <div className="d-flex flex-column gap-2">
                      <button
                        className="btn btn-danger w-100"
                        style={{ height: '35px', borderRadius: '16px', fontSize: '14px', fontWeight: '500', backgroundColor: '#C9194A' }}
                        onClick={() => handleBookNow(doctor)}
                      >
                        Book Now
                      </button>
                      <button
                        className="btn w-100 text-dark"
                        style={{
                          height: '40px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          textDecoration: 'underline',
                        }}
                        onClick={() => handleViewProfile(doctor, getServiceName('shortName'))}
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {loadingMore && (
              <>
                {Array(4).fill(0).map((_, index) => (
                  <div key={`skeleton-${index}`} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ padding: '0 8px' }}>
                    <DoctorCardSkeleton />
                  </div>
                ))}
              </>
            )}
            {!hasMore && filteredDoctors.length > 0 && (
              <div className="col-12 text-center py-3">
                <p className="text-muted">No more doctors to load</p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedDoctor && (
        <BookAppointment
          show={showBooking}
          onClose={handleCloseBooking}
          doctor={selectedDoctor}
          serviceShortName={getServiceName('shortName')}
          serviceName={getServiceName('name')}
        />
      )}
    </div>
  );
};

export default Doctors;